/* eslint-disable max-len */
import React, {
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { Row, Col, Form } from 'react-bootstrap'
import InputField from '@components/common/InputField/InputField.component'
import AddressFields from './AddressFields.component'
import CustomNumericInputField from '@components/common/CustomNumericInputFileld/CustomNumericInputField.component'
import {
  ProviderPersonalInfo as ProviderPersonalInfoType,
  ProviderValidationErrors,
} from '@dataTypes/provider.types'
import {
  Gender,
  MaritalStatus,
  Pronoun,
  ProviderWorkPosition,
} from '@constants/Providers'
import {
  isProviderSeatRoleOptionDisabled,
  shouldRestrictProviderSeatRoles,
} from '@utils/providerRole.util'

export type ProviderPersonalInfoSyncRef = {
  syncAutofill: () => Partial<
    Pick<ProviderPersonalInfoType, 'mobile' | 'phone' | 'work' | 'fax'>
  >
}

type ProviderPersonalInfoProps = {
  providerData: ProviderPersonalInfoType
  handleInputChange: (field: string, value: string | boolean | number) => void
  handleNameBlur: (field: string, value: string) => void
  validationErrors: ProviderValidationErrors
  atProviderSeatLimit?: boolean
  originalWorkRole?: string
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const readDigitsFromWrap = (
  wrapRef: React.RefObject<HTMLDivElement | null>,
): string => {
  const input = wrapRef.current?.querySelector('input')
  const raw = input?.value ?? ''
  const digits = raw.replace(/\D/g, '').slice(0, 10)
  return digits.length === 10 ? digits : ''
}

const ProviderPersonalInfo = forwardRef<
  ProviderPersonalInfoSyncRef,
  ProviderPersonalInfoProps
>(function ProviderPersonalInfo(
  {
    providerData,
    handleInputChange,
    handleNameBlur,
    validationErrors,
    atProviderSeatLimit = false,
    originalWorkRole,
  },
  ref,
) {
  const [emailError, setEmailError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<{
    mobile?: string
    phone?: string
    work?: string
    fax?: string
  }>({})
  const [calculatedAge, setCalculatedAge] = useState<number | null>(null)
  const mobileWrapRef = useRef<HTMLDivElement>(null)
  const phoneWrapRef = useRef<HTMLDivElement>(null)
  const workWrapRef = useRef<HTMLDivElement>(null)
  const faxWrapRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(
    ref,
    () => ({
      syncAutofill: (): Partial<
        Pick<ProviderPersonalInfoType, 'mobile' | 'phone' | 'work' | 'fax'>
      > => {
        const mobile = readDigitsFromWrap(mobileWrapRef)
        const phone = readDigitsFromWrap(phoneWrapRef)
        const work = readDigitsFromWrap(workWrapRef)
        const fax = readDigitsFromWrap(faxWrapRef)
        const out: Partial<
          Pick<ProviderPersonalInfoType, 'mobile' | 'phone' | 'work' | 'fax'>
        > = {}
        if (mobile) out.mobile = mobile
        if (phone) out.phone = phone
        if (work) out.work = work
        if (fax) out.fax = fax
        return out
      },
    }),
    [],
  )

  const validateEmail = useCallback((email: string): boolean => {
    if (!emailRegex.test(email)) {
      setEmailError('Invalid email address')
      return false
    }
    setEmailError(null)
    return true
  }, [])

  const validatePhone = useCallback(
    (phone: string, field: 'mobile' | 'phone' | 'work' | 'fax'): void => {
      const cleanedPhone = phone.replace(/\D/g, '')
      if (cleanedPhone.length !== 10) {
        if (field === 'mobile' || cleanedPhone.length > 0) {
          setPhoneError((prev) => ({
            ...prev,
            [field]: 'Phone number must be exactly 10 digits',
          }))
        } else {
          setPhoneError((prev) => ({ ...prev, [field]: undefined }))
        }
      } else {
        setPhoneError((prev) => ({ ...prev, [field]: undefined }))
      }
    },
    [],
  )

  const handlePhoneChange = (
    value: string,
    field: 'mobile' | 'phone' | 'work' | 'fax',
  ): void => {
    handleInputChange(field, value)
    validatePhone(value, field)
  }

  const syncMobileFromDomAndValidate = useCallback((): void => {
    const input = mobileWrapRef.current?.querySelector('input')
    const raw = input?.value ?? ''
    const digits = raw.replace(/\D/g, '').slice(0, 10)
    if (digits.length === 10) {
      const current = String(providerData.mobile ?? '').replace(/\D/g, '')
      if (digits !== current) {
        handleInputChange('mobile', digits)
      }
      validatePhone(digits, 'mobile')
    } else {
      validatePhone(providerData.mobile ?? '', 'mobile')
    }
  }, [providerData.mobile, handleInputChange, validatePhone])

  const calculateAge = (dateOfBirth: string): number | null => {
    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--
    }
    return age >= 0 ? age : null
  }

  const handleDateOfBirthChange = (value: string): void => {
    handleInputChange('dateOfBirth', value)
    const age = calculateAge(value)
    setCalculatedAge(age)
  }

  return (
    <>
      <Row className="mb-2 gx-2">
        <Col>
          <InputField
            label="First name"
            placeholder="ex. John"
            value={providerData.firstName || ''}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            onBlur={(e) => handleNameBlur('firstName', e.target.value)}
            hint={validationErrors.firstName || undefined}
            destructive={!!validationErrors.firstName}
          />
        </Col>
        <Col>
          <InputField
            label="Last name"
            placeholder="ex. Doe"
            value={providerData.lastName || ''}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            onBlur={(e) => handleNameBlur('lastName', e.target.value)}
            hint={validationErrors.lastName || undefined}
            destructive={!!validationErrors.lastName}
          />
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col>
          <Form.Label>
            <span>
              <span>Middle name</span>
              <small className="text-optional"> (Optional)</small>
            </span>
          </Form.Label>
          <InputField
            placeholder="ex. Knox"
            value={providerData.middleName || ''}
            onChange={(e) => handleInputChange('middleName', e.target.value)}
            onBlur={(e) => handleNameBlur('middleName', e.target.value)}
          />
        </Col>
        <Col>
          <InputField
            label="Username"
            placeholder="Type a username"
            value={providerData.username || ''}
            onChange={(e) => handleInputChange('username', e.target.value)}
            hint={validationErrors.username || undefined}
            destructive={!!validationErrors.username}
          />
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col>
          <Form.Group controlId="formGender">
            <Form.Label>Gender</Form.Label>
            <Form.Select
              value={providerData.gender || ''}
              onChange={(e) => handleInputChange('gender', e.target.value)}
              className={validationErrors.gender ? 'is-invalid' : ''}
            >
              <option value="">Select</option>
              {Object.values(Gender).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
            {validationErrors.gender && (
              <div className="hint-text">{validationErrors.gender}</div>
            )}
          </Form.Group>
        </Col>
        <Col className="dob-field">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <InputField
              label="Date of birth"
              type="date"
              value={providerData.dateOfBirth || ''}
              onChange={(e) => handleDateOfBirthChange(e.target.value)}
              hint={validationErrors.dateOfBirth || undefined}
              destructive={!!validationErrors.dateOfBirth}
              max={new Date().toISOString().split('T')[0]}
            />
            {providerData.dateOfBirth && calculatedAge !== null && (
              <span className="age-display">({calculatedAge} years old)</span>
            )}
          </div>
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col>
          <Form.Group controlId="formPreferredPronouns">
            <Form.Label>
              <span>
                <span>Preferred pronouns</span>
                <small className="text-optional"> (Optional)</small>
              </span>
            </Form.Label>
            <Form.Select
              value={providerData.pronouns || ''}
              onChange={(e) => handleInputChange('pronouns', e.target.value)}
            >
              <option value="">Select</option>
              {Object.values(Pronoun).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="formMaritalStatus">
            <Form.Label>Marital status</Form.Label>
            <Form.Select
              value={providerData.maritalStatus || ''}
              onChange={(e) =>
                handleInputChange('maritalStatus', e.target.value)
              }
              className={validationErrors.maritalStatus ? 'is-invalid' : ''}
            >
              <option value="">Select</option>
              {Object.values(MaritalStatus).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
            {validationErrors.maritalStatus && (
              <div className="hint-text">{validationErrors.maritalStatus}</div>
            )}
          </Form.Group>
        </Col>
      </Row>
      <AddressFields
        providerData={providerData}
        handleInputChange={handleInputChange}
        validationErrors={validationErrors}
      />
      <Row className="mb-2 gx-2">
        <Col>
          <Form.Group>
            <Form.Label>Cellphone</Form.Label>
            <div ref={mobileWrapRef}>
              <CustomNumericInputField
                id="mobile"
                placeholder="(201) 555-0179"
                value={providerData.mobile || ''}
                onChange={(value) => handlePhoneChange(value, 'mobile')}
                onBlur={syncMobileFromDomAndValidate}
                autocomplete="section-mobile tel"
                className={
                  phoneError.mobile || validationErrors.mobile
                    ? 'input-error'
                    : ''
                }
                destructive={!!phoneError.mobile || !!validationErrors.mobile}
              />
            </div>
            {(phoneError.mobile || validationErrors.mobile) && (
              <div className="hint-text">
                {validationErrors.mobile || phoneError.mobile}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col>
          <InputField
            label="Email"
            placeholder="example@email.com"
            type="email"
            value={providerData.email || ''}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={() => validateEmail(providerData.email || '')}
            hint={emailError || validationErrors.email || undefined}
            destructive={!!emailError || !!validationErrors.email}
          />
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col>
          <Form.Group
            className={
              validationErrors.phone || phoneError.phone ? 'is-invalid' : ''
            }
          >
            <Form.Label>
              <span>
                <span>Home number</span>
                <small className="text-optional"> (Optional)</small>
              </span>
            </Form.Label>
            <div ref={phoneWrapRef}>
              <CustomNumericInputField
                id="phone"
                placeholder="(201) 555-0180"
                value={providerData.phone || ''}
                onChange={(value) => handlePhoneChange(value, 'phone')}
                autocomplete="section-home tel"
                className={phoneError.phone ? 'input-error' : ''}
                destructive={!!phoneError.phone}
              />
            </div>
            {phoneError.phone && (
              <div className="hint-text">{phoneError.phone}</div>
            )}
          </Form.Group>
        </Col>
        <Col>
          <Form.Group
            className={
              validationErrors.work || phoneError.work ? 'is-invalid' : ''
            }
          >
            <Form.Label>
              <span>
                <span>Work contact</span>
                <small className="text-optional"> (Optional)</small>
              </span>
            </Form.Label>
            <div ref={workWrapRef}>
              <CustomNumericInputField
                id="work"
                placeholder="(201) 555-0181"
                value={providerData.work || ''}
                onChange={(value) => handlePhoneChange(value, 'work')}
                autocomplete="section-work tel"
                className={phoneError.work ? 'input-error' : ''}
                destructive={!!phoneError.work}
              />
            </div>
            {phoneError.work && (
              <div className="hint-text">{phoneError.work}</div>
            )}
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className={phoneError.fax ? 'is-invalid' : ''}>
            <Form.Label>
              <span>
                <span>Fax number</span>
                <small className="text-optional"> (Optional)</small>
              </span>
            </Form.Label>
            <div ref={faxWrapRef}>
              <CustomNumericInputField
                id="fax"
                placeholder="(201) 555-0182"
                value={providerData.fax || ''}
                onChange={(value) => handlePhoneChange(value, 'fax')}
                autocomplete="section-fax tel"
                className={phoneError.fax ? 'input-error' : ''}
                destructive={!!phoneError.fax}
              />
            </div>
            {phoneError.fax && (
              <div className="hint-text">{phoneError.fax}</div>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col md={6}>
          <Form.Group controlId="formWorkRolePersonal">
            <Form.Label>Work role</Form.Label>
            <Form.Select
              value={providerData.workRole ?? ''}
              onChange={(e) => handleInputChange('workRole', e.target.value)}
            >
              <option value="">Select</option>
              {Object.values(ProviderWorkPosition).map((value) => (
                <option
                  key={value}
                  value={value}
                  disabled={isProviderSeatRoleOptionDisabled(
                    atProviderSeatLimit,
                    originalWorkRole,
                    value,
                  )}
                >
                  {value}
                </option>
              ))}
            </Form.Select>
            {shouldRestrictProviderSeatRoles(
              atProviderSeatLimit,
              originalWorkRole,
            ) && (
              <div className="small text-danger mt-2" role="status">
                <strong>Note:</strong> Provider limit reached. Kindly contact
                your superadmin.
              </div>
            )}
            {validationErrors.workRole && (
              <div className="error-text">{validationErrors.workRole}</div>
            )}
          </Form.Group>
        </Col>
      </Row>
    </>
  )
})

export default ProviderPersonalInfo
